import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import breakpoint from '~/utils/style-utils';
import getRouletteBgColor from '~/components/casino-shared/utilities/get-roulette-bg-color';
import { RouletteNumber } from '~/components/live-casino/types';

const slideUp = keyframes`
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideRight = keyframes`
  0% {
    transform: translateX(-20px);
  }
  100% {
    transform: translateX(0);
  }
`;

const RouletteNumbersWrapper = styled.div`
	display: flex;
	gap: 4px;
	justify-content: center;
	align-items: center;
	height: 36px;
	padding: 6px 0;
	position: relative;
	${breakpoint.lg`
   	gap: 12px;
  `}
`;

const RouletteNumberPill = styled.div<{ number: string; animation: ReturnType<typeof keyframes> }>`
	min-width: 18px;
	height: 18px;
	background-color: ${props => getRouletteBgColor(props.number)};
	color: ${props => (props.number === '0' ? 'rgb(0, 199, 107)' : 'white')};
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 4px;
	font: var(--small-bold);
	position: relative;
	animation: ${props => props.animation} 0.5s ease-out;

	&:first-of-type {
		border: 1px solid rgba(255, 255, 255, 1);
	}
`;

const MultiplierWrapper = styled.div`
	background-color: yellow;
	color: black;
	font-size: 8px;
	position: absolute;
	bottom: 15px;
	left: 50%;
	transform: translateX(-50%);
	width: auto;
	line-height: 12px;
	height: 12px;
	padding: 0 1px;
	border-radius: 2px;
	${breakpoint.lg`
		font-size: 10px;
  `}
`;

/**
 * Finds the starting index where two arrays of objects overlap, based on the `value` field.
 *
 * @param arr1 - The first array of objects with a `value` field.
 * @param arr2 - The second array of objects with a `value` field.
 * @returns The index at which the two arrays overlap, or -1 if no overlap is found.
 */
const findOverlapIndex = (arr1: RouletteNumber[], arr2: RouletteNumber[]): number => {
	// Extract `value` fields as arrays of strings
	const oldArr = arr1.map(item => item.value);
	const newArr = arr2.map(item => item.value);

	// If both arrays are completely identical, overlap starts from index 0
	if (JSON.stringify(oldArr) === JSON.stringify(newArr)) {
		return 0;
	}

	// Iterate through the length of the arrays to find the overlap
	for (let i = 1; i < oldArr.length; i += 1) {
		// Remove the last `i` elements from the old array
		const trimmedOldArr = oldArr.slice(0, oldArr.length - i);
		// Remove the first `i` elements from the new array
		const trimmedNewArr = newArr.slice(i);

		// Check if the remaining portions of the arrays are equal
		if (JSON.stringify(trimmedOldArr) === JSON.stringify(trimmedNewArr)) {
			return i;
		}
	}

	return -1; // Return -1 if no overlap is found
};

const RouletteNumbers = ({ rouletteNumbers }: { rouletteNumbers: RouletteNumber[] }) => {
	const [oldWinningNumbers, setOldWinningNumbers] = useState<RouletteNumber[]>(rouletteNumbers);
	const [newFromIndex, setNewFromIndex] = useState<number | null>(null);

	// compare old and new winning numbers to decide which ones will have which animation
	useEffect(() => {
		const index = findOverlapIndex(oldWinningNumbers, rouletteNumbers);
		setNewFromIndex(index);
		setOldWinningNumbers(rouletteNumbers);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rouletteNumbers]);

	return (
		<RouletteNumbersWrapper>
			{rouletteNumbers?.slice(0, 7).map((rouletteNumber, i) => (
				<RouletteNumberPill
					// eslint-disable-next-line react/no-array-index-key
					key={`roulette-number-${rouletteNumber.value as unknown as string}-${i}`}
					number={rouletteNumber.value as unknown as string}
					animation={newFromIndex && i < newFromIndex ? slideUp : slideRight}
				>
					{rouletteNumber.value}
					{rouletteNumber.multiplier && <MultiplierWrapper>x{rouletteNumber.multiplier}</MultiplierWrapper>}
				</RouletteNumberPill>
			))}
		</RouletteNumbersWrapper>
	);
};

export default RouletteNumbers;
